import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { IMAGES } from "../data/site";
import { STEEL_SECTORS } from "../data/steel";
import { MaskedLine, Reveal, Eyebrow } from "../components/Reveal";
import { Marquee } from "../components/Marquee";

export default function Steel() {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 500], [0, 140]);

  return (
    <>
      <section data-testid="steel-hero" className="relative isolate flex min-h-[70vh] items-end overflow-hidden noise-surface">
        <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10">
          <img src={IMAGES.steelPlant} alt="Steel plant with molten metal" className="h-[115%] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A101D] via-[#0A101D]/70 to-[#0A101D]/40" />
        </motion.div>
        <div className="mx-auto w-full max-w-7xl px-5 pb-16 pt-40 lg:px-8">
          <Eyebrow>Steel trading</Eyebrow>
          <h1 className="mt-5 font-display text-5xl font-bold uppercase leading-[0.98] text-white sm:text-7xl">
            <MaskedLine delay={0.15}>Steel for energy,</MaskedLine>
            <MaskedLine delay={0.3} innerClassName="text-amber-500">water & construction.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-300"
          >
            Line pipe, structural steels, TMT bars and piling pipes — sourced from qualified mills and shipped to
            project sites and fabricators worldwide, with mill test certificates on every lot.
          </motion.p>
        </div>
      </section>

      <Marquee items={["API 5L Line Pipe", "Structural Steels EN / ASTM / IS", "TMT Bars Fe 415 — 550", "Seamless & ERW Piling Pipes", "Mill Test Certificates"]} />

      <section data-testid="steel-overview" className="bg-white py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-2 lg:px-8">
          <div>
            <Reveal><Eyebrow>Overview</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-display text-4xl font-bold uppercase leading-tight text-[#0E1526] sm:text-5xl">
                Mill-direct steel, project-ready
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-6 text-base leading-relaxed text-slate-700">
                Terra Bloom trades steel for the sectors that cannot afford delays: energy pipelines, water
                distribution, construction and deep foundations. We source from qualified mills, arrange inspection
                and documentation, and deliver to site or port on project schedules.
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                Every lot ships with its mill test certificate; third-party inspection is available on request.
                Grades, sizes and delivery terms are quoted against your project specification.
              </p>
            </Reveal>
          </div>
          <Reveal className="relative">
            <div className="clipped-frame overflow-hidden">
              <img src={IMAGES.steelBeams} alt="Structural steel beams" className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105" />
            </div>
            <div className="absolute -bottom-6 -right-2 sm:-right-6 border border-amber-500/40 bg-white px-6 py-4">
              <p className="font-display text-4xl font-bold text-amber-500">4</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Sectors served</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="steel-sectors" className="border-y border-black/10 bg-[#F5F5F2] py-24 md:py-32 noise-surface">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><Eyebrow>What we trade</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 max-w-2xl font-display text-4xl font-bold uppercase leading-tight text-[#0E1526] sm:text-5xl">
              Steel by sector & standard
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {STEEL_SECTORS.map((s, i) => (
              <Reveal key={s.n} delay={(i % 2) * 0.1}>
                <Link
                  to={`/steel/${s.slug}`}
                  data-testid={`steel-sector-${s.slug}`}
                  className="group block h-full border border-black/10 bg-white transition-colors hover:border-amber-500/50"
                >
                  <div className="clipped-frame h-44 overflow-hidden">
                    <img src={s.image} alt={s.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-8">
                    <div className="flex items-baseline justify-between">
                      <span className="font-mono text-xs text-amber-600">{s.n}.</span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{s.sub}</span>
                    </div>
                    <h3 className="mt-3 font-display text-2xl font-semibold uppercase tracking-wide text-[#0E1526] transition-colors group-hover:text-amber-600">
                      {s.shortName}
                    </h3>
                    <ul className="mt-5 space-y-3">
                      {s.points.map((pt) => (
                        <li key={pt} className="flex gap-3 text-xs leading-relaxed text-slate-600">
                          <ArrowUpRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                          <span>{pt}</span>
                        </li>
                      ))}
                    </ul>
                    <span className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 transition-colors group-hover:text-amber-600">
                      View specs <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="steel-cta" className="bg-white py-24 text-center">
        <Reveal><Eyebrow className="text-center">Project requirements?</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mx-auto mt-4 max-w-2xl font-display text-4xl font-bold uppercase leading-tight text-[#0E1526] sm:text-5xl">
            Send your steel specification
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-slate-600">
            Share grades, sizes, quantities and delivery point — our desk replies within one business day with
            mill options, pricing and freight.
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <Link
            to="/contact?product=Steel%20products"
            data-testid="steel-quote-button"
            className="group mt-10 inline-flex items-center gap-2 bg-amber-500 px-10 py-4 text-sm font-semibold text-[#0A101D] transition-colors hover:bg-amber-400"
          >
            Request a Steel Quote <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </>
  );
}
